import Ember from 'ember';
import { inject as service } from '@ember/service';
import Route from 'cio/routes/base';
import { action } from '@ember/object';
import { getAccessCookie } from 'cio/utils/auth-cookies';
import SessionService from 'cio/services/session';
import RavenService from 'cio/services/raven';
import RouterService from '@ember/routing/router-service';
import AuthService from 'cio/services/auth';
import { ApiResponse, ApiError } from 'cio/services/api';
import Transition from '@ember/routing/-private/transition';
import MasqService from 'cio/services/masq';
import MomentService from 'cio/services/moment';
import AlertsService from 'cio/services/alerts';
import AccountService from 'cio/services/account';
import PersistentFeaturesService from 'cio/services/persistent-features';
import tracking from 'cio/utils/tracking-scripts';
import config from 'cio/config/environment';
import AnalyticsService from 'cio/services/analytics';
import {
  announceConfirmTransition,
  subscribeToTransitionAttempt,
  setChildAppLoaded,
  setActiveRoute,
  activeRouteStore,
} from '@customerio/hydra-nav-state';
import { plumaContextValueStore, plumaIconManagerStore } from '@customerio/hydra-pluma-state';
// import { plumaContextValueStore, plumaThemeStore, plumaIconManagerStore } from '@customerio/hydra-pluma-state';
import type { PlumaContextValue } from '@customerio/pluma-components/ember';
import type { PlumaTheme } from '@customerio/pluma-components/css';
import type { IconManager } from '@customerio/pluma-icons';
import HydraService from 'cio/services/hydra';
import { isDestroyed } from '@ember/destroyable';
import PerformanceService, { PerformanceMetric } from 'cio/services/performance';
import ApplicationController from './controller';

// native window has to still be here, to avoid breaking event listeners from being attached to it:
const nativeWindow = window;

const OFFLINE_MESSAGE = `It looks like you've disconnected from the internet.
                         Check your network connection`;

const DOCS_LINK_SUFFIXES: string[] = [
  'https://learn.customer.io',
  'https://customer.io/docs',
  'https://segment.com/integrations',
];

export default class ApplicationRoute extends Route {
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service account!: AccountService;
  @service hydra!: HydraService;
  @service moment!: MomentService;
  @service masq!: MasqService;
  @service raven!: RavenService;
  @service session!: SessionService;
  @service router!: RouterService;
  @service persistentFeatures!: PersistentFeaturesService;
  @service performance!: PerformanceService;

  declare controller: ApplicationController;

  async beforeModel(transition: Transition) {
    await this.session.setup();

    if (!getAccessCookie()) {
      return super.beforeModel(transition);
    }

    // if ember-simple-auth session is not already present, generate new one using _access cookie
    // (done by calling cookie-access-token authenticator)
    if (!this.session.isAuthenticated) {
      await this.session.authenticate('authenticator:cookie-access-token');
    }

    return super.beforeModel(transition);
  }

  afterModel() {
    this.persistentFeatures.loadAnonymousFeatures();

    // Inject any tracking scripts needed on app load.
    //
    // Here, we inject only if the app isn't authenticated. In case the session
    // is authenticated, the authenticated route will make this call with some
    // extra context that's available to it (the current account).
    if (!this.session.isAuthenticated) {
      tracking.onAppLoad({ authenticated: false });
    }
  }

  hydraUnsubscribeListeners: (() => void)[] = [];
  internalRouteUpdate = false;

  activate() {
    super.activate();

    this._trackDocumentationLinkClicks = this._trackDocumentationLinkClicks.bind(this);
    document.addEventListener('click', this._trackDocumentationLinkClicks);

    nativeWindow.addEventListener('online', this.handleOnline);
    nativeWindow.addEventListener('offline', this.handleOffline);

    if (this.hydra.isHydraEnabled) {
      this.hydraUnsubscribeListeners.push(subscribeToTransitionAttempt(this.onAttemptTransition));
      this.router.on('routeDidChange', this.onRouteChange);
      this.hydraUnsubscribeListeners.push(activeRouteStore.listen(this.onActiveRouteChange));
      if (plumaContextValueStore != null) {
        this.hydraUnsubscribeListeners.push(plumaContextValueStore.listen(this.#onPlumaContextValueChange));
      }
      // TODO: Uncomment when Hydra is also updated to the latest Pluma
      // Components
      // this.hydraUnsubscribeListeners.push(plumaThemeStore.listen(this.#onPlumaThemeChange));
      this.hydraUnsubscribeListeners.push(plumaIconManagerStore.listen(this.#onPlumaIconManagerChange));
      setChildAppLoaded(true);
    }
  }

  deactivate() {
    super.deactivate();
    document.removeEventListener('click', this._trackDocumentationLinkClicks);
    nativeWindow.removeEventListener('online', this.handleOnline);
    nativeWindow.removeEventListener('offline', this.handleOffline);

    if (this.hydra.isHydraEnabled) {
      // @ts-ignore TS complains about "off" not existing, but it does exist
      this.router.off('routeDidChange', this.onRouteChange);
      this.hydraUnsubscribeListeners.forEach((unsubscribe) => unsubscribe());
      this.hydraUnsubscribeListeners = [];
    }
  }

  _trackDocumentationLinkClicks(ev: Event) {
    if (!ev.target || (ev.target as HTMLElement).tagName.toLowerCase() !== 'a') {
      return;
    }

    let href = (ev.target as HTMLElement).getAttribute('href');
    if (!href) {
      return;
    }

    if (!DOCS_LINK_SUFFIXES.some((suffix) => (href as string).startsWith(suffix))) {
      return;
    }

    this.analytics.track('user', 'help_docs_viewed', { href, routeName: this.router.currentRouteName });
  }

  #onPlumaContextValueChange = (value: PlumaContextValue) => {
    if (this.controller == null) {
      return;
    }
    this.controller.plumaContextValue = value;
  };

  #onPlumaThemeChange = (theme: PlumaTheme) => {
    if (this.controller == null) {
      return;
    }
    this.controller.plumaTheme = theme;
  };

  #onPlumaIconManagerChange = (iconManager: IconManager) => {
    if (this.controller == null) {
      return;
    }
    this.controller.plumaIconManager = iconManager;
  };

  @action
  handleOnline() {
    this.alerts.remove('online.status.failure');
  }

  @action
  handleOffline() {
    this.alerts.sticky('online.status.failure', OFFLINE_MESSAGE);
  }

  @action
  error(error: ApiResponse) {
    if (config.isDevelopment || Ember.testing) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    let errors: ApiError[] = (error.errors as ApiError[]) || [];
    let specificError = errors[0] || { status: 'unknown' };

    // Unauthorised requests are handled by application Ember Data adapter, conditionally cause a logout
    if (specificError.status === '401') {
      return;
    }

    // Report any unknown errors (i.e. not HTTP requests)
    if (specificError.status === 'unknown') {
      this.raven.captureException(error as any);
    }

    this.router.transitionTo('errors', specificError.status);
  }

  /** Currently, this function measures only the initial load performance. */
  @action
  measurePerformance(transition: Transition) {
    if (transition == null) {
      return;
    }

    if (transition.from == null || transition.from.name === 'loading') {
      this.performance.recordMetric(PerformanceMetric.LoadingStateEnd);
    }
  }

  @action
  onRouteChange(transition: Transition) {
    if (isDestroyed(this)) {
      return;
    }

    this.measurePerformance(transition);

    this.internalRouteUpdate = true;

    if (this.hydra.isHydraEnabled && this.router.currentURL != null) {
      setActiveRoute(this.router.currentURL);
    }
  }

  @action
  onActiveRouteChange(activeRoute: string) {
    if (isDestroyed(this)) {
      return;
    }

    let { internalRouteUpdate } = this;
    this.internalRouteUpdate = false;

    if (internalRouteUpdate) {
      return;
    }

    let targetRoute = this.router.recognize(activeRoute);

    if (targetRoute == null) {
      return;
    }

    this.router.transitionTo(activeRoute);
  }

  @action
  onAttemptTransition() {
    if (!this.hydra.isHydraEnabled) {
      return;
    }
    // Don't confirm the transition if we're within the "app" route structure
    // - the "app" route has its own route confirmation action which uses
    // confirmChanges before allowing a transition
    if (this.router.isActive('app')) {
      return;
    }

    announceConfirmTransition();
  }
}
